import React, {CSSProperties, HTMLAttributes, ReactNode, Ref, useLayoutEffect, useRef, useState} from 'react'

import {Colors, Icon, Info, ToolTip} from '../index.js'
import {caret, field, formColors, getFieldBackground, getFieldBorderColor, getFieldCursor, getFieldTextColor, invalidIcon, label as labelTokens} from './styles.js'
import {ensureFieldStyles} from './fieldStyles.js'

// The ::placeholder colour of the controls (fieldControlClass), inline styles can't set it
ensureFieldStyles()

export interface FloatingLabelProps {
    label?: ReactNode
    info?: ReactNode
    infoIcon?: string
    /** Small label at the top of the field instead of a placeholder-like label on the text line */
    floated?: boolean
    /** Leaves room for a prepend while the label is not floated */
    prepend?: boolean
    /** Gives the label the field's background over the full width, so text scrolled under it (multiline) is hidden */
    opaque?: boolean
    /** Fixed left padding (resting and floated), instead of the one from the tokens */
    paddingLeft?: number
    disabled?: boolean
    readOnly?: boolean
    /** Id of the labelable control (<input>, <textarea>) the label belongs to, clicking the label focuses it */
    htmlFor?: string
    /** Id of the label itself, for a control that isn't labelable (a focusable div) and uses aria-labelledby */
    id?: string
    onClick?: () => void
}

export function FloatingLabel({label, info, infoIcon, floated, prepend, opaque, paddingLeft, disabled, readOnly, htmlFor, id, onClick}: FloatingLabelProps) {
    const style: CSSProperties = {
        display: 'flex',
        margin: 0,
        fontWeight: 'normal',
        alignItems: 'center',
        position: 'absolute',
        paddingLeft: paddingLeft ?? (prepend && !floated ? labelTokens.paddingLeftPrepend : labelTokens.paddingLeft),
        top: floated ? labelTokens.topFloated : labelTokens.top,
        fontSize: floated ? labelTokens.fontSizeFloated : labelTokens.fontSize,
        color: formColors.label,
        cursor: getFieldCursor({disabled, readOnly}),
        transition: labelTokens.transition
    }

    if (opaque) {
        style.width = '100%'
        style.background = getFieldBackground({disabled, readOnly})
    }

    return (
        <label style={style} htmlFor={htmlFor} id={id} onClick={onClick}>
            <span style={{whiteSpace: 'nowrap'}}>{label}</span>
            {/* Above the box: a positioned element in it (the Popover wrapper of a Select) would cover the icon and its hover */}
            {info && <Info style={{marginLeft: 6, position: 'relative', zIndex: 1}} icon={infoIcon} text={info} />}
        </label>
    )
}

export interface InvalidIconProps {
    /** A string is shown as the message, otherwise the generic 'required' message */
    invalid: boolean | string
    /** Width of content at the right of the field (append, caret, buttons) the icon has to stay left of */
    trailingWidth?: number
    /** Icon class instead of the default alert icon */
    icon?: string
}

/** The alert icon of an invalid field, at the right end of its first line. Positioned against the field's outer container. */
export function InvalidIcon({invalid, trailingWidth = 0, icon}: InvalidIconProps) {
    return (
        <div
            style={{
                position: 'absolute',
                top: invalidIcon.top,
                right: invalidIcon.right + trailingWidth,
                width: invalidIcon.size,
                height: invalidIcon.size,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1
            }}
        >
            <ToolTip
                text={typeof invalid === 'string' ? invalid : 'Dit veld is verplicht'}
                placement='bottom'
                style={{width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: -10}}
            >
                <Icon
                    size='md'
                    icon={icon || 'mdi mdi-alert-circle'}
                    color={formColors.borderInvalid}
                    style={{color: formColors.borderInvalid, marginRight: 0}}
                />
            </ToolTip>
        </div>
    )
}

export interface FieldCaretProps {
    disabled?: boolean
    readOnly?: boolean
    onClick?: () => void
}

/** The drop-down caret at the right of a field that opens a list or popover. Put it in FieldBox's children and pass
 * trailingWidth so the invalid icon stays left of it. Positioned against the field's outer container. */
export function FieldCaret({disabled, readOnly, onClick}: FieldCaretProps) {
    return (
        <i
            style={{position: 'absolute', right: caret.right, top: caret.top, color: getFieldTextColor({disabled, readOnly})}}
            onClick={onClick}
            className={caret.icon}
        />
    )
}

function Prepend({prepend}: {prepend: ReactNode}) {
    if (typeof prepend === 'string' && prepend.includes('mdi')) {
        return <Icon size='sm' style={{marginRight: 6, position: 'absolute', left: 6, bottom: 6}} icon={prepend} />
    }

    if (typeof prepend === 'string') {
        return (
            <div
                style={{
                    position: 'absolute',
                    left: 6,
                    bottom: 6,
                    fontSize: 14,
                    fontWeight: 400,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 20,
                    height: 20
                }}
            >
                {prepend}
            </div>
        )
    }

    return <div style={{position: 'absolute', left: 2, bottom: 3}}>{prepend}</div>
}

/** A text append (a unit) instead of an icon or a node */
export function isTextAppend(append: ReactNode): append is string {
    return typeof append === 'string' && !append.includes('mdi')
}

/**
 * A text append sits inside the box next to the control, so the control gets narrower instead of running under the
 * text, and longer units like 'per 100km' fit
 */
const TextAppend = React.forwardRef<HTMLDivElement, {append: string}>(function TextAppend({append}, ref) {
    return (
        <div
            ref={ref}
            style={{
                alignSelf: 'stretch',
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
                whiteSpace: 'nowrap',
                paddingTop: field.paddingTop,
                paddingBottom: field.paddingBottom,
                paddingLeft: 4,
                paddingRight: field.paddingX,
                fontSize: field.fontSize,
                fontWeight: 400,
                color: Colors.textMedium
            }}
        >
            {append}
        </div>
    )
})

function Append({append}: {append: ReactNode}) {
    if (typeof append === 'string' && append.includes('mdi')) {
        return <Icon size='md' style={{marginRight: 6, position: 'absolute', right: 10, bottom: 10}} icon={append} />
    }

    // Text (a unit like 'km' or 'per 100km') is rendered inside the box by TextAppend
    if (isTextAppend(append)) {
        return null
    }

    return <div style={{position: 'absolute', right: 2, bottom: 10}}>{append}</div>
}

/**
 * The shared look of every text-like field: outer container (margins, flex), floating label, bordered box,
 * prepend/append and invalid icon. The component puts its actual control in children, which are laid out in a row
 * inside the box (display flex, vertically centred). The box has no padding of its own: a control that fills the box
 * (Input's <input>) carries the text padding itself, other content can set it with boxStyle (field.paddingX etc.).
 *
 * Absolutely positioned children are placed against the outer container, i.e. the outer edge of the box including its
 * border, the same as prepend/append and the invalid icon. Keep the box itself unpositioned: the label comes before it
 * in the DOM (for tab order) and is only painted on top because the box is static. Wrap content in an element of its
 * own with position relative when it needs to be positioned against something else.
 *
 * Uses:
 * - Input: children is a transparent full-size <input> (+ the password toggle)
 * - Select: children are the value overlay or pills row, a narrow transparent <input>, caret, copy button and spinner;
 *   boxStyle sets paddingTop/paddingRight; trailingWidth keeps the invalid icon left of the caret/copy button
 * - TextArea: multiline (box grows from field.height), the <textarea> carries paddingTop 18; opaqueLabel hides scrolled text
 * - Steps: children are the −/+ IconButtons around the <input>; trailingWidth for the + button; labelPaddingLeft keeps
 *   the label above the number
 * - IconPicker: boxProps makes the box a focusable role=button; children are the chosen icon and the FieldCaret
 */
export interface FieldBoxProps {
    label?: ReactNode
    info?: ReactNode
    infoIcon?: string
    /** Shows the label small at the top, e.g. when focused or when there is a value or placeholder */
    floated?: boolean
    /** Gives the box the focus border colour */
    focused?: boolean
    /** Shows the invalid border colour and icon; a string is shown as the message */
    invalid?: boolean | string
    disabled?: boolean
    readOnly?: boolean
    /** The box grows with its content from field.height instead of having a fixed height */
    multiline?: boolean
    /** Content at the left of the box: an 'mdi ...' icon class, a short text (e.g. a currency sign) or a node */
    prepend?: ReactNode
    /** Content at the right of the box, in the same forms as prepend */
    append?: ReactNode
    /** Width of extra content at the right (append, caret, buttons) the invalid icon has to stay left of */
    trailingWidth?: number
    /** Icon class of the invalid icon instead of the default alert icon */
    invalidIconClass?: string
    /** Label with the field's background over the full width, for content that scrolls under it */
    opaqueLabel?: boolean
    /** Fixed left padding of the label, resting and floated, e.g. to keep it above text that starts after a button */
    labelPaddingLeft?: number
    /** Id of the <input>/<textarea> in children: the label is a <label htmlFor>, so clicking it focuses the control */
    controlId?: string
    /** Id for the label, for a control that isn't labelable (a focusable div) and points at it with aria-labelledby */
    labelId?: string
    /** Only for a control a <label htmlFor> can't focus (a focusable div), labelable controls use controlId */
    onLabelClick?: () => void
    /** Style of the outer container (size, margins, flex), merged last */
    style?: CSSProperties
    /** Style of the bordered box, merged last */
    boxStyle?: CSSProperties
    /** Extra attributes for the box, e.g. role, tabIndex, aria-*, onClick, onMouseDown, onFocus, onBlur */
    boxProps?: HTMLAttributes<HTMLDivElement>
    /** The box element, e.g. to anchor a popover to the field */
    boxRef?: Ref<HTMLDivElement>
    children?: ReactNode
}

export default function FieldBox(props: FieldBoxProps) {
    const {label, info, infoIcon, floated, focused, invalid, disabled, readOnly, multiline, prepend, append, trailingWidth = 0, invalidIconClass, opaqueLabel, labelPaddingLeft, controlId, labelId, onLabelClick, style, boxStyle, boxProps, boxRef, children} = props

    // The invalid icon has to stay left of a text append, whose width depends on the text
    const textAppendRef = useRef<HTMLDivElement>(null)
    const [textAppendWidth, setTextAppendWidth] = useState(0)
    useLayoutEffect(() => {
        const width = textAppendRef.current ? textAppendRef.current.offsetWidth - invalidIcon.right : 0
        if (width !== textAppendWidth) {
            setTextAppendWidth(width)
        }
    })

    const containerStyle: CSSProperties = {
        flex: style?.width ? undefined : 1,
        flexShrink: 0,
        position: 'relative',
        marginLeft: field.marginLeft,
        marginRight: field.marginRight,
        marginBottom: field.marginBottom,
        height: multiline ? undefined : field.height,
        minHeight: multiline ? field.height : undefined,
        ...style
    }

    const fieldBoxStyle: CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        height: multiline ? undefined : field.height,
        minHeight: multiline ? field.height : undefined,
        borderBottom: `1px solid ${getFieldBorderColor({disabled, readOnly, invalid: !!invalid, focused})}`,
        borderRadius: field.borderRadius,
        background: getFieldBackground({disabled, readOnly}),
        color: getFieldTextColor({disabled, readOnly}),
        cursor: getFieldCursor({disabled, readOnly}),
        ...boxStyle
    }

    return (
        <div style={containerStyle}>
            <FloatingLabel
                label={label}
                info={info}
                infoIcon={infoIcon}
                floated={floated}
                prepend={!!prepend}
                opaque={opaqueLabel}
                paddingLeft={labelPaddingLeft}
                disabled={disabled}
                readOnly={readOnly}
                htmlFor={controlId}
                id={labelId}
                onClick={onLabelClick}
            />

            <div {...boxProps} style={fieldBoxStyle} ref={boxRef}>
                {children}

                {isTextAppend(append) && <TextAppend ref={textAppendRef} append={append} />}
            </div>

            {prepend && <Prepend prepend={prepend} />}

            {append && <Append append={append} />}

            {invalid && <InvalidIcon invalid={invalid} trailingWidth={trailingWidth + textAppendWidth} icon={invalidIconClass} />}
        </div>
    )
}
